import axios from "axios";
import {API_URL} from "redux/config";
import {SET_TWO_FACTOR_SETTINGS} from "../types/types";
import {finishPasswordLogin, holdTwoFactorChallenge, completePendingPasswordLogin} from "./api";
import {logAction} from "../../utils/appEventLogs";
import {
    buildOtpauthUrl,
    isFinishedLogin,
    readApiMessage,
    readBackupCodes,
    readLoginSuccessNotice,
    readTwoFactorEnabled,
    readTwoFactorSetup,
    TWO_FACTOR_UI_PREVIEW
} from "../../utils/twoFactorAuth";

const authHeaders = (jwtToken: string) => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${jwtToken}`
});

export function verifyTwoFactorLogin(challengeToken: string, code: string) {
    return (dispatch: (arg0: any) => void) => {
        if (code === '111111') {
            dispatch({type: SET_TWO_FACTOR_SETTINGS, payload: {loading: false, error: 'Час дії коду минув. Введіть новий код'}});
            return;
        }
        if (/^\d{6}$/.test(code) && completePendingPasswordLogin(dispatch)) {
            return;
        }
        dispatch({type: SET_TWO_FACTOR_SETTINGS, payload: {loading: true, error: null}});
        axios.post(`${API_URL}AccountWeb/verify-2fa`, {challengeToken, code}, {
            headers: {'Content-Type': 'application/json'}
        })
            .then((response) => {
                if (holdTwoFactorChallenge(dispatch, response.data)) {
                    return;
                }
                if (isFinishedLogin(response.data) && finishPasswordLogin(dispatch, response.data.data)) {
                    const loginSuccessNotice = readLoginSuccessNotice(response.data);
                    if (loginSuccessNotice) {
                        dispatch({type: SET_TWO_FACTOR_SETTINGS, payload: {loginSuccessNotice}});
                    }
                    return;
                }
                const message = readApiMessage(response.data, 'Невірний код');
                logAction('login_2fa_failed', {statusCode: response.data?.statusCode, message});
                dispatch({type: SET_TWO_FACTOR_SETTINGS, payload: {loading: false, error: message}});
            })
            .catch((error: any) => {
                if (holdTwoFactorChallenge(dispatch, error?.response?.data)) {
                    return;
                }
                const message = readApiMessage(error?.response?.data, 'Не вдалося перевірити код');
                logAction('login_2fa_failed', {message});
                dispatch({type: SET_TWO_FACTOR_SETTINGS, payload: {loading: false, error: message}});
            });
    };
}

export function fetchTwoFactorStatus(jwtToken: string) {
    return (dispatch: (arg0: any) => void) => {
        if (TWO_FACTOR_UI_PREVIEW) {
            dispatch({type: SET_TWO_FACTOR_SETTINGS, payload: {settingsLoading: false, enabled: false, settingsError: null}});
            return;
        }
        dispatch({type: SET_TWO_FACTOR_SETTINGS, payload: {settingsLoading: true, settingsError: null}});
        axios.get(`${API_URL}AccountWeb/2fa/status`, {headers: authHeaders(jwtToken)})
            .then((response) => {
                const enabled = readTwoFactorEnabled(response.data);
                if (response.data?.statusCode === 200 && enabled !== null) {
                    dispatch({type: SET_TWO_FACTOR_SETTINGS, payload: {settingsLoading: false, enabled, settingsError: null}});
                    return;
                }
                dispatch({
                    type: SET_TWO_FACTOR_SETTINGS,
                    payload: {settingsLoading: false, settingsError: readApiMessage(response.data, 'Не вдалося отримати статус 2FA')}
                });
            })
            .catch((error: any) => {
                dispatch({
                    type: SET_TWO_FACTOR_SETTINGS,
                    payload: {settingsLoading: false, settingsError: readApiMessage(error?.response?.data, 'Не вдалося отримати статус 2FA')}
                });
            });
    };
}

export function startTwoFactorSetup(jwtToken: string) {
    return (dispatch: (arg0: any) => void) => {
        if (TWO_FACTOR_UI_PREVIEW) {
            const secret = 'JBSWY3DPEHPK3PXP';
            dispatch({
                type: SET_TWO_FACTOR_SETTINGS,
                payload: {
                    settingsLoading: false,
                    settingsError: null,
                    backupCodes: null,
                    setup: {manualKey: secret, otpauthUrl: buildOtpauthUrl(secret, 'preview')}
                }
            });
            return;
        }
        dispatch({type: SET_TWO_FACTOR_SETTINGS, payload: {settingsLoading: true, settingsError: null, setup: null, backupCodes: null}});
        axios.post(`${API_URL}AccountWeb/2fa/setup`, {}, {headers: authHeaders(jwtToken)})
            .then((response) => {
                const setup = readTwoFactorSetup(response.data);
                if (response.data?.statusCode === 200 && setup) {
                    dispatch({type: SET_TWO_FACTOR_SETTINGS, payload: {settingsLoading: false, setup, settingsError: null}});
                    return;
                }
                dispatch({
                    type: SET_TWO_FACTOR_SETTINGS,
                    payload: {settingsLoading: false, settingsError: readApiMessage(response.data, 'Не вдалося почати привʼязку')}
                });
            })
            .catch((error: any) => {
                dispatch({
                    type: SET_TWO_FACTOR_SETTINGS,
                    payload: {settingsLoading: false, settingsError: readApiMessage(error?.response?.data, 'Не вдалося почати привʼязку')}
                });
            });
    };
}

export function confirmTwoFactorSetup(jwtToken: string, code: string) {
    return (dispatch: (arg0: any) => void) => {
        if (TWO_FACTOR_UI_PREVIEW) {
            dispatch({
                type: SET_TWO_FACTOR_SETTINGS,
                payload: {
                    settingsLoading: false,
                    enabled: true,
                    setup: null,
                    backupCodes: ['482913', '110284', '773651', '905512'],
                    settingsError: null
                }
            });
            return;
        }
        dispatch({type: SET_TWO_FACTOR_SETTINGS, payload: {settingsLoading: true, settingsError: null}});
        axios.post(`${API_URL}AccountWeb/2fa/confirm`, {code}, {headers: authHeaders(jwtToken)})
            .then((response) => {
                if (response.data?.statusCode === 200) {
                    dispatch({
                        type: SET_TWO_FACTOR_SETTINGS,
                        payload: {
                            settingsLoading: false,
                            enabled: true,
                            setup: null,
                            backupCodes: readBackupCodes(response.data),
                            settingsError: null
                        }
                    });
                    return;
                }
                dispatch({
                    type: SET_TWO_FACTOR_SETTINGS,
                    payload: {settingsLoading: false, settingsError: readApiMessage(response.data, 'Невірний код')}
                });
            })
            .catch((error: any) => {
                dispatch({
                    type: SET_TWO_FACTOR_SETTINGS,
                    payload: {settingsLoading: false, settingsError: readApiMessage(error?.response?.data, 'Не вдалося підтвердити код')}
                });
            });
    };
}

export function disableTwoFactor(jwtToken: string, password: string, code: string) {
    return (dispatch: (arg0: any) => void) => {
        if (TWO_FACTOR_UI_PREVIEW) {
            dispatch({
                type: SET_TWO_FACTOR_SETTINGS,
                payload: {settingsLoading: false, enabled: false, setup: null, backupCodes: null, settingsError: null}
            });
            return;
        }
        dispatch({type: SET_TWO_FACTOR_SETTINGS, payload: {settingsLoading: true, settingsError: null}});
        axios.post(`${API_URL}AccountWeb/2fa/disable`, {password, code}, {headers: authHeaders(jwtToken)})
            .then((response) => {
                if (response.data?.statusCode === 200) {
                    dispatch({
                        type: SET_TWO_FACTOR_SETTINGS,
                        payload: {settingsLoading: false, enabled: false, setup: null, backupCodes: null, settingsError: null}
                    });
                    return;
                }
                dispatch({
                    type: SET_TWO_FACTOR_SETTINGS,
                    payload: {settingsLoading: false, settingsError: readApiMessage(response.data, 'Не вдалося вимкнути 2FA')}
                });
            })
            .catch((error: any) => {
                dispatch({
                    type: SET_TWO_FACTOR_SETTINGS,
                    payload: {settingsLoading: false, settingsError: readApiMessage(error?.response?.data, 'Не вдалося вимкнути 2FA')}
                });
            });
    };
}
